import { FeatureAppStepCitaMode, NormalizedVoucher, Order, Vehicle } from '@esportal/portal-service';
import {
	Layout,
	Breakpoints,
	Container,
	ContainerPadding,
	CTA,
	TokenTextAppearance,
	TextAlignment,
	TokenTextColor,
	Image,
	Text,
	Richtext,
} from '@volkswagen-onehub/components-core';
import { ChevronLeft } from '@volkswagen-onehub/icons-core';
import React from 'react';
import styled from 'styled-components';
import { COLOR_TOKEN } from '../../helpers/color-tokens';
import { IMAGE_DEFAULT_VWLOGO } from '../../vw-logo';
import { PortalContext } from '../../context/portal-context';
import { useNavigate } from 'react-router-dom';
import { SalesforceVouchersDetailDataInfo } from './components/salesforce-voucher-detail-data-info';
import { useSetState } from '../../hooks/get-state-hook';
import { StepCita } from './components/step-cita';
import { VouchersHelper } from './helper/salesforce-voucher-helper';
import { normalizeMarkdownForRichtext, createRichtextTextOverrides } from '../../helpers/common-helpers';
import { useBenefitsTracker } from '../benefits/vouchers-promotions/hooks/use-benefits-tracker';

const ContentWrapper = styled.div`
	max-width: 960px;
	margin: 0 auto;
`;

const VouchersDetailContainer = styled.section`
	position: relative;
`;

const ImageStyled = styled(Image)`
	border-radius: 16px;
	object-position: 'top';
	align-self: center;

	/* @media screen and (max-width: 1279px) {
    aspect-ratio: 16/9;
  }
  @media screen and (min-width: 1280px) {
    aspect-ratio: 5/2;
  } */
`;

const VouchersCarInfo = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 16px;
	left: 16px;
	padding: 4px 4px;
	border-radius: 18px;
	border: 1px solid ${COLOR_TOKEN.focus.main};
	background-color: ${COLOR_TOKEN.content.onsurface};
`;

const ImageWrapper = styled.div`
	position: relative;
`;

const StatusIndicator = styled.div<{ status: string }>`
	background: ${(props: { status: string }) => props.status};
	border-radius: 50%;
	width: 0.9em;
	height: 0.9em;
`;

export const SalesforceVoucherDetail: React.FC<{ voucher: NormalizedVoucher }> = ({ voucher }) => {
	const { portalService } = React.useContext(PortalContext);
	const userVehicle: Vehicle | Order | null = voucher.vin
		? portalService.getVehicleOrOrderByVin(voucher.vin).getValue()
		: null;
	const detailContainer = React.useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	const [stepCitaFAs, setStepCitaFAs, getStepCitaFAs] = useSetState<JSX.Element[]>([]);

	const { trackSalesforceVoucherDetailBackClick } = useBenefitsTracker();

	const VouchersCarInfohandleClickBackVouchers = () => {
		trackSalesforceVoucherDetailBackClick(voucher);
		navigate('/dashboard');
	};

	const handleClickRequestAppointment = async (mode: FeatureAppStepCitaMode) => {
		setStepCitaFAs([...(await getStepCitaFAs()), <StepCita key={mode} voucher={voucher} mode={mode} />]);
	};

	return (
		<VouchersDetailContainer ref={detailContainer}>
			{voucher && (
				<Layout
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 1 },
							{ name: 'a', columns: 22 },
							{ name: '.', columns: 1 },
						],
					}}
				>
					<ContentWrapper>
						<Container
							padding={{
								bottom: ContainerPadding.dynamic0100,
								top: ContainerPadding.dynamic0100,
							}}
							wrap={'always'}
						>
							<CTA
								tag="a"
								onClick={VouchersCarInfohandleClickBackVouchers}
								icon={<ChevronLeft variant="small" />}
								iconPosition="start"
								emphasis="tertiary"
								size="small"
							>
								Volver a la vista general
							</CTA>
						</Container>

						<Container
							padding={{
								bottom: ContainerPadding.dynamic0100,
							}}
							horizontalAlign={'center'}
							wrap={'always'}
						>
							<ImageWrapper>
								{userVehicle && (
									<VouchersCarInfo data-testid="Etiqueta">
										<Text appearance={TokenTextAppearance.label100} bold>
											{(userVehicle as Vehicle).vgedModelGroup || (userVehicle as Order).vgedCommunicationModel}
										</Text>
									</VouchersCarInfo>
								)}
								<ImageStyled src={voucher.card.imageUrl || IMAGE_DEFAULT_VWLOGO} objectFit="cover" alt="Imagen" />
							</ImageWrapper>

							<Container
								padding={{
									top: ContainerPadding.dynamic0130,
									bottom: ContainerPadding.dynamic0130,
								}}
								wrap={'always'}
							>
								<Text
									appearance={TokenTextAppearance.headline300}
									textAlign={TextAlignment.center}
									color={TokenTextColor.primary}
								>
									<Richtext
										markdown={normalizeMarkdownForRichtext(voucher.card.title)}
										overrides={createRichtextTextOverrides(TokenTextAppearance.headline300, TokenTextColor.primary)}
									/>
								</Text>
								<Container
									shrinkContent
									padding={{
										top: ContainerPadding.static150,
									}}
									verticalAlign="center"
									horizontalAlign="center"
								>
									<Container
										padding={{
											right: ContainerPadding.static150,
										}}
									>
										<StatusIndicator status={VouchersHelper.getVoucherStatusColor(voucher?.status)}></StatusIndicator>
									</Container>

									<Text
										appearance={TokenTextAppearance.copy200}
										color={TokenTextColor.primary}
										textAlign={TextAlignment.center}
									>
										{VouchersHelper.getValidityMessage(voucher?.status, voucher?.expirationDate, voucher?.enjoyedDate)}
									</Text>
								</Container>
							</Container>

							<SalesforceVouchersDetailDataInfo
								voucher={voucher}
								handleClickRequestAppointment={handleClickRequestAppointment}
							/>
						</Container>
					</ContentWrapper>
				</Layout>
			)}

			{stepCitaFAs}
		</VouchersDetailContainer>
	);
};
